import "../locales";
import * as React from "react";
import { PostHogProvider } from "posthog-js/react";
import { Outlet } from "react-router-dom";
import { useUserSlice } from "@/models/useUserSlice";
import { useUISlice } from "@/models/useUISlice";
import AlertWidgetCreateNoteMediaImport from "@/ui/AlertWidgetCreateNoteMediaImport";
import AlertWidgetStartUpload from "@/ui/AlertWidgetStartUpload";
import AlertPromptSignIn from "@/ui/AlertPromptSignIn";
import AlertWidgetCreateNoteLiveTranscription from "@/ui/AlertWidgetCreateNoteLiveTranscription";
import AlertWidgetCreateNoteVoiceRecording from "@/ui/AlertWidgetCreateNoteVoiceRecording";
import AlertModal from "@/ui/AlertModal";
import { useToast } from "@/models/useToast";
import AlertPrompt from "@/ui/AlertPrompt";
import AlertWidgetManageFolders from "@/ui/AlertWidgetManageFolders";
import AlertExportTranscriptOptions from "@/ui/AlertExportTranscriptOptions";
import AlertSelect from "@/ui/AlertSelect";
import AlertWidgetChat from "@/ui/AlertWidgetChat";
import AlertWidgetSearch from "@/ui/AlertWidgetSearch";
import AlertWidgetCreateNoteText from "@/ui/AlertWidgetCreateNoteText";
import AlertWidgetCreateNotePDForImageImport from "@/ui/AlertWidgetCreateNotePDForImageImport";
import AlertWidgetPaywall from "@/ui/AlertWidgetPaywall";
import AlertWidgetTranscriptRenameSpeakers from "@/ui/AlertWidgetTranscriptRenameSpeakers";
import AlertWidgetAIContentEditWithAI from "@/ui/AlertWidgetAIContentEditWithAI";
import { MOBILE_MAX_WIDTH } from "@/constants";
import AlertWidgetCreateNote from "@/ui/AlertWidgetCreateNote";
import AlertWidgetAIContentTemplates from "@/ui/AlertWidgetAIContentTemplates";
import { SafeArea } from "capacitor-plugin-safe-area";
import MigrationToNewVersion from "@/ui/MigrationToNewVersion";

const HelmetProvider = React.lazy(() =>
  import("react-helmet-async").then((m) => ({ default: m.HelmetProvider }))
);

const GoogleOAuthProvider = React.lazy(() =>
  import("@react-oauth/google").then((m) => ({
    default: m.GoogleOAuthProvider,
  }))
);

// Lazy load analytics
const loadAnalytics = () => {
  import("@/fetchData/gtag").then((gtag) => {
    gtag.pageview(window.location.pathname);
  });
  import("@/fetchData/fpixel").then((fbq) => {
    fbq.pageview();
  });
  import("@/fetchData/datadog");
};

export default function MyApp() {
  const _hydrated = useUserSlice((state) => state._hasHydrated);
  const { ToastContainer } = useToast();

  React.useEffect(() => {
    loadAnalytics();

    const handleResize = async () => {
      const { insets } = await SafeArea.getSafeAreaInsets();
      useUISlice.getState().setDevice({
        isMobile: window.innerWidth < MOBILE_MAX_WIDTH,
        width: window.innerWidth,
        height: window.innerHeight,
        safeArea: { top: insets.top, bottom: insets.bottom },
      });
    };

    handleResize();
    const handlePopState = () => {
      loadAnalytics();
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return <MigrationToNewVersion />;

  if (!_hydrated) {
    return null;
  }

  return (
    <React.Suspense>
      <HelmetProvider>
        <PostHogProvider
          apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
          options={{
            api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
            disable_session_recording: true,
          }}
        >
          <GoogleOAuthProvider
            clientId={import.meta.env.VITE_PUBLIC_GOOGLE_CLIENT_ID}
          >
            <Outlet />
            <AlertWidgetChat />
            <AlertWidgetSearch />
            <AlertWidgetManageFolders />
            <AlertWidgetCreateNoteMediaImport />
            <AlertWidgetCreateNoteLiveTranscription />
            <AlertWidgetCreateNoteVoiceRecording />
            <AlertWidgetCreateNoteText />
            <AlertWidgetCreateNotePDForImageImport />
            <AlertWidgetTranscriptRenameSpeakers />
            <AlertWidgetStartUpload />
            <AlertWidgetAIContentTemplates />
            <AlertWidgetAIContentEditWithAI />
            <AlertModal />
            <AlertPrompt />
            <ToastContainer />
            <AlertSelect />
            <AlertWidgetCreateNote />
            <AlertExportTranscriptOptions />
            <AlertWidgetPaywall />
            <AlertPromptSignIn />
          </GoogleOAuthProvider>
        </PostHogProvider>
      </HelmetProvider>
    </React.Suspense>
  );
}
